import { useState } from 'react'
import {
  Box,
  Card,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded'
import TrendingDownRounded from '@mui/icons-material/TrendingDownRounded'
import type { SimulationResults } from '../api/types'
import { formatEUR, formatEUR2, formatPct } from '../format'
import { Mode } from './resultsMode'

function Stat({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string
  value: string
  hint?: string
  tone?: 'default' | 'positive' | 'negative'
}) {
  const color =
    tone === 'positive' ? 'success.main' : tone === 'negative' ? 'error.main' : 'text.primary'
  const TrendIcon =
    tone === 'positive' ? TrendingUpRounded : tone === 'negative' ? TrendingDownRounded : null
  return (
    <Stack sx={{ flex: '1 1 0', minWidth: 110, px: { xs: 1.5, sm: 2 }, py: 1.5 }}>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          lineHeight: 1.2,
          fontSize: { xs: '0.65rem', sm: '0.75rem' },
          overflowWrap: 'anywhere',
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        {TrendIcon && <TrendIcon sx={{ color, fontSize: 16 }} />}
        <Typography variant="h6" sx={{ color, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Stack>
      {hint && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {hint}
        </Typography>
      )}
    </Stack>
  )
}

export default function ResultsPanel({
  r,
  compact = false,
  mode: modeProp,
  onModeChange,
  hideToggle = false,
}: {
  r: SimulationResults
  compact?: boolean
  mode?: Mode
  onModeChange?: (mode: Mode) => void
  hideToggle?: boolean
}) {
  const [internalMode, setInternalMode] = useState<Mode>(Mode.Rental)
  const mode = modeProp ?? internalMode
  const setMode = onModeChange ?? setInternalMode

  const toggle = (
    <ToggleButtonGroup
      value={mode}
      exclusive
      size="small"
      onChange={(_, value) => value && setMode(value)}
    >
      <ToggleButton value={Mode.Rental}>Location</ToggleButton>
      <ToggleButton value={Mode.Resale}>Revente</ToggleButton>
    </ToggleButtonGroup>
  )

  const stats =
    mode === Mode.Rental
      ? [
          <Stat
            key="cashflow"
            label={compact ? 'Cash-flow / mois' : 'Cash-flow mensuel'}
            value={formatEUR2(r.monthlyCashFlow)}
            tone={r.monthlyCashFlow >= 0 ? 'positive' : 'negative'}
            hint={compact ? undefined : `${formatEUR(r.annualCashFlow)} / an`}
          />,
          <Stat key="brut" label="Rendement brut" value={formatPct(r.grossYield)} />,
          <Stat
            key="net"
            label={compact ? 'Rendement net' : 'Rendement net de charge'}
            value={formatPct(r.netYield)}
          />,
          <Stat key="fin" label="Rendement financier" value={formatPct(r.financialYield)} />,
          ...(compact
            ? []
            : [
                <Stat
                  key="mensualite"
                  label="Mensualité crédit"
                  value={formatEUR2(r.monthlyLoanPayment)}
                />,
                <Stat
                  key="cout"
                  label="Coût de l'investissement"
                  value={formatEUR(r.investmentCost)}
                />,
                <Stat key="interets" label="Intérêts totaux" value={formatEUR(r.totalInterest)} />,
              ]),
        ]
      : [
          <Stat
            key="estimation"
            label="Estimation de revente"
            value={formatEUR(r.resaleEstimate)}
          />,
          <Stat
            key="margeBrute"
            label="Marge brute"
            value={formatEUR(r.grossMargin)}
            tone={r.grossMargin >= 0 ? 'positive' : 'negative'}
          />,
          <Stat
            key="margeBrutePct"
            label="Marge brute (%)"
            value={formatPct(r.grossMarginPercent)}
          />,
          <Stat
            key="margeNetteChargePct"
            label="Marge nette de charge (%)"
            value={formatPct(r.netMarginChargePercent)}
          />,
          <Stat
            key="margeAvantImpots"
            label="Marge nette avant impôts"
            value={formatEUR(r.netMarginBeforeTax)}
            tone={r.netMarginBeforeTax >= 0 ? 'positive' : 'negative'}
          />,
          <Stat
            key="marge"
            label="Marge nette"
            value={formatEUR(r.netMargin)}
            tone={r.netMargin >= 0 ? 'positive' : 'negative'}
          />,
          <Stat key="roi" label="ROI du projet" value={formatPct(r.roi)} />,
          <Stat
            key="annualise"
            label="Rentabilité annualisée"
            value={formatPct(r.annualizedReturn)}
          />,
        ]

  const statsRow = (
    <Stack
      direction="row"
      divider={<Divider orientation="vertical" flexItem />}
      sx={{ flexWrap: 'wrap' }}
    >
      {stats}
    </Stack>
  )

  if (compact) {
    return (
      <div>
        {!hideToggle && <Box sx={{ mb: 1.5 }}>{toggle}</Box>}
        {statsRow}
      </div>
    )
  }

  return (
    <Card>
      {!hideToggle && (
        <>
          <Stack
            direction="row"
            sx={{
              px: 2,
              py: 1.5,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Rentabilité
            </Typography>
            {toggle}
          </Stack>
          <Divider />
        </>
      )}
      {statsRow}
    </Card>
  )
}
